import { useEffect, useRef } from "react";

/**
 * Fecha o overlay com Esc. Renderize dentro do overlay condicional:
 *   {open && <div ...><EscClose onEsc={close} />...</div>}
 * Pilha compartilhada: com modais empilhados, só o mais recente fecha.
 */
const escStack: Array<{ current: () => void }> = [];
function onGlobalEsc(e: KeyboardEvent) {
  if (e.key !== "Escape" || escStack.length === 0) return;
  e.stopImmediatePropagation();
  escStack[escStack.length - 1].current();
}

export function EscClose({ onEsc }: { onEsc: () => void }) {
  const fn = useRef(onEsc);
  fn.current = onEsc;
  useEffect(() => {
    const entry = { current: () => fn.current() };
    if (escStack.length === 0) document.addEventListener("keydown", onGlobalEsc, true);
    escStack.push(entry);
    return () => {
      const i = escStack.indexOf(entry);
      if (i >= 0) escStack.splice(i, 1);
      if (escStack.length === 0) document.removeEventListener("keydown", onGlobalEsc, true);
    };
  }, []);
  return null;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Acessibilidade básica de dialog para os shells de modal do app:
 * - Esc fecha
 * - foco entra no dialog ao abrir e volta ao trigger ao fechar
 * - Tab fica preso dentro do dialog (focus trap)
 *
 * Uso: const ref = useDialog(onClose); <div ref={ref} role="dialog" aria-modal="true">...
 */
export function useDialog(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const previous = document.activeElement as HTMLElement | null;

    // foco inicial: primeiro focável, senão o próprio container
    const first = el.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? el).focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !el) return;
      const items = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (i) => i.offsetParent !== null,
      );
      if (items.length === 0) { e.preventDefault(); return; }
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === firstItem || active === el)) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && active === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    }

    el.addEventListener("keydown", onKeyDown);
    return () => {
      el.removeEventListener("keydown", onKeyDown);
      previous?.focus?.();
    };
  }, []);

  return ref;
}
